import { createContext, useContext } from "react";

export type FieldValue = FormDataEntryValue | FormDataEntryValue[] | null;

export type FieldValidator = (value: FieldValue, formData: FormData) => boolean;

export interface RegisteredFieldValidation {
    name: string;
    validate: FieldValidator;
    message?: string;
}

export type RegisterFieldValidation = (
    validation: RegisteredFieldValidation
) => () => void;

export interface FormFieldErrorContextValue {
    getFieldError: (name: string) => string | null;
    clearFieldError: (name: string) => void;
}

export const FormValidationContext =
    createContext<RegisterFieldValidation | null>(null);

export const FormFieldErrorContext =
    createContext<FormFieldErrorContextValue | null>(null);

export const useRegisterFieldValidation = (): RegisterFieldValidation | null => {
    return useContext(FormValidationContext);
};

export const useFieldError = (name?: string): string | null => {
    const context = useContext(FormFieldErrorContext);

    if (!context || !name) {
        return null;
    }

    return context.getFieldError(name);
};

export const useClearFieldError = (): ((name: string) => void) => {
    const context = useContext(FormFieldErrorContext);

    return (name: string): void => {
        if (!context) {
            return;
        }

        context.clearFieldError(name);
    };
};

export const resolveFieldValue = (
    formData: FormData,
    name: string
): FieldValue => {
    const values = formData.getAll(name);

    if (values.length === 0) {
        return null;
    }

    if (values.length === 1) {
        return values[0] ?? null;
    }

    return values;
};

export const asString = (value: FieldValue): string => {
    if (Array.isArray(value)) {
        const firstValue = value[0];
        return typeof firstValue === "string" ? firstValue : "";
    }

    return typeof value === "string" ? value : "";
};

export const asBoolean = (value: FieldValue): boolean => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return value !== null;
};

